import { Sel, STag } from '#~/stag/tag.js';
import { Loading } from './loading.jsx';
import type { BrandProps } from './types.js';
import { useConfirmMakeModel } from './utils.js';
import * as Select from '@studio/design-system/select';
import { castId } from '@studio/models';
import type { Brand as M_Brand } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isEmpty } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { memo, useMemo } from 'react';

/** `<Brand>` component. */
export const Brand = memo(
  ({ brands, selection, size, confirmOther = true, ...props }: BrandProps): ReactElement => {
    const confirmMakeModel = useConfirmMakeModel();
    const handleValueChange = useLatestFn((nextValue: string) => {
      const nextBrand = castId<M_Brand['id']>(nextValue);

      if (nextBrand === selection.value) {
        return;
      }

      const brand = brands?.items[nextBrand];
      const isOther = brand?.name?.toLocaleLowerCase().includes('other');

      if (isOther && confirmOther) {
        confirmMakeModel({
          onOkay: () => selection(nextBrand)
        });
      } else {
        selection(nextBrand);
      }
    });

    return (
      <Select.Root onValueChange={handleValueChange} size={size} value={selection.value}>
        <Select.Trigger {...props} {...STag(Sel.ConfigVideoDeviceBrandModel, 'brand')}>
          <Select.Value placeholder={isEmpty(brands) ? <Loading /> : 'Select a brand'} />
        </Select.Trigger>

        <Select.Content>
          {useMemo(() => {
            let other: T_Maybe<M_Brand>;

            const items = Object.values(brands?.items ?? []).filter((µ) => {
              if (µ.name.toLowerCase().includes('other')) {
                other = µ;

                return false;
              }

              return true;
            });

            return (
              <>
                {items.map((µ) => (
                  <Select.Item
                    key={µ.id}
                    value={µ.id}
                    {...STag(Sel.ConfigVideoDeviceBrandModel, 'brand', µ.id)}
                  >
                    {µ.name}
                  </Select.Item>
                ))}
                {!!other && (
                  <Select.Item
                    key={other.id}
                    value={other.id}
                    {...STag(Sel.ConfigVideoDeviceBrandModel, 'brand', other.id)}
                  >
                    <Select.ItemText>{other.name} - brand is not listed</Select.ItemText>
                  </Select.Item>
                )}
              </>
            );
          }, [brands])}
        </Select.Content>
      </Select.Root>
    );
  },
  // Include brands: a selection.value-only compare skipped the render when
  // the cold registry fetch completed, leaving an empty dropdown forever.
  // Per rule: investigate-before-adding (ai.knowledge/global) — Model already
  // compares its collection; Brand must match that parity.
  (prev, next) =>
    prev.brands === next.brands &&
    prev.selection.value === next.selection.value &&
    prev.size === next.size
);
